import React, { useState, useRef } from 'react'
import { X, Link, Image, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react'
import { useAuth } from '../hooks/useAuth.jsx'
import { leather, paper, fonts, shadows } from '../styles/theme.js'

const TABS = [
  { id: 'url', label: '🔗 From URL', icon: Link },
  { id: 'screenshot', label: '📸 Screenshot', icon: Image },
  { id: 'pinterest', label: '📌 Pinterest', icon: null },
]

export default function ImportModal({ onClose, onImported }) {
  const { authFetch } = useAuth()
  const [tab, setTab] = useState('url')
  const [urlInput, setUrlInput] = useState('')
  const [pinterestInput, setPinterestInput] = useState('')
  const [file, setFile] = useState(null)
  const [status, setStatus] = useState(null) // null | 'loading' | 'polling' | 'done' | 'error'
  const [error, setError] = useState('')
  const [jobId, setJobId] = useState(null)
  const fileRef = useRef(null)
  const pollRef = useRef(null)

  const reset = () => { setStatus(null); setError(''); setJobId(null); clearInterval(pollRef.current) }

  const pollJob = (id) => {
    setStatus('polling')
    pollRef.current = setInterval(async () => {
      try {
        const res = await authFetch(`/api/recipes/import/${id}`)
        if (res.ok) {
          const d = await res.json()
          if (d.status === 'done') { clearInterval(pollRef.current); setStatus('done'); setTimeout(onImported, 1500) }
          if (d.status === 'error') { clearInterval(pollRef.current); setStatus('error'); setError(d.error_message || 'Import failed') }
        }
      } catch { clearInterval(pollRef.current); setStatus('error'); setError('Connection lost') }
    }, 2000)
  }

  const handleUrlImport = async () => {
    if (!urlInput.trim()) return
    setStatus('loading'); setError('')
    try {
      const res = await authFetch('/api/recipes/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'url', sourceData: urlInput }),
      })
      if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
      const d = await res.json()
      setJobId(d.jobId)
      pollJob(d.jobId)
    } catch (e) { setStatus('error'); setError(e.message || 'Import failed') }
  }

  const handleScreenshotImport = async () => {
    if (!file) return
    setStatus('loading'); setError('')
    const formData = new FormData()
    formData.append('image', file)
    formData.append('source', 'screenshot')
    try {
      const res = await authFetch('/api/recipes/import', { method: 'POST', body: formData })
      if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
      const d = await res.json()
      setJobId(d.jobId)
      pollJob(d.jobId)
    } catch (e) { setStatus('error'); setError(e.message || 'Import failed') }
  }

  const handlePinterestImport = async () => {
    if (!pinterestInput.trim()) return
    setStatus('loading'); setError('')
    try {
      const res = await authFetch('/api/recipes/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'pinterest', sourceData: pinterestInput }),
      })
      if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
      const d = await res.json()
      setJobId(d.jobId)
      pollJob(d.jobId)
    } catch (e) { setStatus('error'); setError(e.message || 'Import failed') }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, animation: 'fadeIn 0.2s ease' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, background: paper.bgWhite, borderRadius: 12, overflow: 'hidden', boxShadow: shadows.modal }}>
        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${leather.bg}, ${leather.bgHi})`, padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `2px solid ${leather.gold}` }}>
          <h2 style={{ fontFamily: fonts.cursive, fontSize: 24, color: leather.gold }}>Import Recipe</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color={leather.goldDim} /></button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${paper.borderMed}` }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); reset() }} style={{
              flex: 1, padding: '10px 4px', background: tab === t.id ? paper.bgWhite : paper.bgDark,
              border: 'none', borderBottom: tab === t.id ? `2px solid ${paper.ink}` : '2px solid transparent',
              cursor: 'pointer', fontFamily: fonts.serif, fontSize: 14,
              color: tab === t.id ? paper.ink : paper.inkFaint, transition: 'all 0.15s',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 24 }}>
          {/* Status states */}
          {status === 'loading' && <StatusView label="Submitting import job…" icon="⏳" />}
          {status === 'polling' && <StatusView label="Processing recipe… this may take a moment" icon="🔄" />}
          {status === 'done' && <StatusView label="Recipe imported! Adding to your collection…" icon="✅" />}
          {status === 'error' && (
            <div>
              <StatusView label={error} icon="❌" />
              <button onClick={reset} style={{ marginTop: 12, ...btnStyle() }}>Try Again</button>
            </div>
          )}

          {/* URL tab */}
          {!status && tab === 'url' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontFamily: fonts.serif, fontSize: 15, color: paper.inkLight }}>
                Paste any recipe URL and we'll extract the ingredients and steps.
              </p>
              <input
                value={urlInput} onChange={e => setUrlInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleUrlImport()}
                placeholder="https://www.foodnetwork.com/recipe/..."
                style={inputStyle()}
              />
              <button onClick={handleUrlImport} disabled={!urlInput.trim()} style={btnStyle(!urlInput.trim())}>
                <ArrowRight size={16} /> Import Recipe
              </button>
            </div>
          )}

          {/* Screenshot tab */}
          {!status && tab === 'screenshot' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontFamily: fonts.serif, fontSize: 15, color: paper.inkLight }}>
                Upload a photo of a recipe (cookbook, handwritten, printed) and Claude will extract it.
              </p>
              <div
                onClick={() => fileRef.current?.click()}
                style={{
                  border: `2px dashed ${file ? paper.ink : paper.borderMed}`, borderRadius: 8,
                  padding: 24, textAlign: 'center', cursor: 'pointer', background: paper.bgDark,
                  transition: 'border-color 0.2s',
                }}
              >
                {file ? (
                  <div>
                    <p style={{ fontFamily: fonts.serif, fontSize: 16, color: paper.ink }}>{file.name}</p>
                    <p style={{ fontFamily: fonts.serif, fontSize: 13, color: paper.inkFaint }}>{(file.size / 1024).toFixed(0)} KB</p>
                  </div>
                ) : (
                  <div>
                    <Image size={32} color={paper.borderMed} style={{ marginBottom: 8 }} />
                    <p style={{ fontFamily: fonts.serif, fontSize: 15, color: paper.inkFaint }}>
                      Click to choose image (JPG, PNG, WEBP)
                    </p>
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
              <button onClick={handleScreenshotImport} disabled={!file} style={btnStyle(!file)}>
                <ArrowRight size={16} /> Extract Recipe
              </button>
            </div>
          )}

          {/* Pinterest tab */}
          {!status && tab === 'pinterest' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontFamily: fonts.serif, fontSize: 15, color: paper.inkLight }}>
                Paste a Pinterest pin URL to import the recipe.
              </p>
              <input
                value={pinterestInput} onChange={e => setPinterestInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handlePinterestImport()}
                placeholder="https://www.pinterest.com/pin/..."
                style={inputStyle()}
              />
              <button onClick={handlePinterestImport} disabled={!pinterestInput.trim()} style={btnStyle(!pinterestInput.trim())}>
                <ArrowRight size={16} /> Import from Pinterest
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function StatusView({ label, icon }) {
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
      <p style={{ fontFamily: fonts.serif, fontSize: 16, color: paper.ink }}>{label}</p>
    </div>
  )
}

function inputStyle() {
  return {
    padding: '10px 14px', fontFamily: fonts.serif, fontSize: 15,
    border: `1px solid ${paper.borderMed}`, borderRadius: 8,
    background: paper.bgWhite, color: paper.ink, outline: 'none', width: '100%',
  }
}
function btnStyle(disabled) {
  return {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px', borderRadius: 8,
    background: disabled ? paper.borderMed : paper.ink,
    border: 'none', color: 'white', cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: fonts.serif, fontSize: 16, transition: 'background 0.15s',
  }
}
